import React, { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getUserOrders } from '../../services/order.service'

const Progress = () => {
  const { data: ongoingOrdersData, isLoading } = useQuery({
    queryKey: ['orders', 'progress-stats'],
    queryFn: () => getUserOrders({ type: 'purchases' })
  })

  // Group real data from backend
  const projectData = useMemo(() => {
    const orders = ongoingOrdersData?.orders || []
    
    const stats = {
      'in-progress': { count: 0, percentage: 45 },
      'completed': { count: 0, percentage: 100 },
      'pending': { count: 0, percentage: 15 },
      'cancelled': { count: 0, percentage: 100 }
    }

    orders.forEach(order => {
      if (stats[order.status]) {
        stats[order.status].count++
      }
    })

    return [
      {
        title: "In progress",
        totalProjects: stats['in-progress'].count,
        percentage: stats['in-progress'].percentage,
        label: "Project"
      },
      {
        title: "Completed",
        totalProjects: stats['completed'].count,
        percentage: stats['completed'].percentage,
        label: "Project"
      },
      {
        title: "In Review",
        totalProjects: stats['pending'].count,
        percentage: stats['pending'].percentage,
        label: "Project"
      },
      {
        title: "Cancelled",
        totalProjects: stats['cancelled'].count,
        percentage: stats['cancelled'].percentage,
        label: "Project"
      }
    ]
  }, [ongoingOrdersData])

  const [currentIndex, setCurrentIndex] = useState(0)
  const [animatedCount, setAnimatedCount] = useState(0)
  const [animatedPercentage, setAnimatedPercentage] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const currentData = projectData[currentIndex]

  // Auto cycle through data every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true)
      
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % projectData.length)
        setIsTransitioning(false)
      }, 300)
    }, 3000)

    return () => clearInterval(interval)
  }, [projectData.length])

  // Animate values when data changes
  useEffect(() => {
    if (!currentData) return

    // Reset and animate count
    setAnimatedCount(0)
    const countDuration = 800
    const countSteps = Math.max(1, Math.min(15, currentData.totalProjects))
    const countIncrement = currentData.totalProjects / countSteps
    let currentCount = 0

    const countInterval = setInterval(() => {
      currentCount += countIncrement
      if (currentCount >= currentData.totalProjects) {
        setAnimatedCount(currentData.totalProjects)
        clearInterval(countInterval)
      } else {
        setAnimatedCount(Math.floor(currentCount))
      }
    }, countDuration / countSteps)

    // Reset and animate percentage
    setAnimatedPercentage(0)
    const percentageDuration = 1200
    const percentageSteps = 24
    const percentageIncrement = (currentData.percentage || 0) / percentageSteps
    let currentPercentage = 0

    const percentageInterval = setInterval(() => {
      currentPercentage += percentageIncrement
      if (currentPercentage >= currentData.percentage) {
        setAnimatedPercentage(currentData.percentage)
        clearInterval(percentageInterval)
      } else {
        setAnimatedPercentage(Math.floor(currentPercentage))
      }
    }, percentageDuration / percentageSteps)

    return () => {
      clearInterval(countInterval)
      clearInterval(percentageInterval)
    }
  }, [currentIndex, currentData])

  // Calculate stroke dash array for circle animation
  const radius = 35
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (animatedPercentage / 100) * circumference

  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center h-full bg-gray-100 dark:bg-[#1d2026] p-2 border border-gray-100 dark:border-[#32353c] rounded-2xl">
        <div className="bg-[#174568] dark:bg-[#112d45] rounded-[10px] w-full p-6 w-64 h-48 animate-pulse flex items-center justify-center">
           <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full flex items-center justify-center h-full bg-gray-100 dark:bg-[#1d2026] p-2 border border-gray-100 dark:border-[#32353c] rounded-2xl">
      <div 
        className={`text-white rounded-[10px] w-full p-6 w-64 h-48 relative overflow-hidden transform transition-all duration-500 bg-[#174568] dark:bg-[#112d45] ${
          isTransitioning ? 'scale-95 opacity-80' : 'scale-100 opacity-100'
        }`}
      >
        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-between">
          {/* Header */}
          <div className="space-y-1">
            <h3 
              className={`text-sm font-medium opacity-90 tracking-wide transition-all duration-300 ${
                isTransitioning ? 'translate-y-2 opacity-0' : 'translate-y-0 opacity-90'
              }`}
            >
              {currentData.title}
            </h3>
            
            {/* Animated Count */}
            <div className="text-4xl font-bold leading-none">
              <span 
                className={`inline-block transition-all duration-500 ${
                  isTransitioning ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-100'
                }`}
              >
                {animatedCount.toString().padStart(2, '0')}
              </span>
            </div>
          </div>

          {/* Bottom Section with Circle Progress */}
          <div className="flex items-center justify-between">
            {/* Animated Circular Progress */}
            <div className="relative">
              <svg 
                className="transform -rotate-90 w-20 h-20"
                viewBox="0 0 80 80"
              >
                {/* Background Circle */}
                <circle
                  cx="40"
                  cy="40"
                  r={radius}
                  stroke="#546FFF"
                  strokeWidth="6"
                  fill="transparent"
                  className="opacity-20"
                />
                
                {/* Progress Circle */}
                <circle
                  cx="40"
                  cy="40"
                  r={radius}
                  stroke="#546FFF"
                  strokeWidth="6"
                  fill="transparent"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-1200 ease-out"
                  style={{
                    filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))'
                  }}
                />
              </svg>
              
              {/* Percentage Text */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span 
                  className={`text-lg font-bold transition-all duration-300 ${
                    isTransitioning ? 'scale-75 opacity-0' : 'scale-100 opacity-100'
                  }`}
                >
                  {animatedPercentage}%
                </span>
              </div>

              {/* Animated Dot */}
              <div 
                className="absolute w-2 h-2 bg-[#546FFF] rounded-full shadow-lg"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: `translate(-50%, -50%) rotate(${(animatedPercentage / 100) * 360}deg) translateY(-${radius}px)`,
                  transition: 'transform 1200ms ease-out',
                  boxShadow: '0 0 8px rgba(255, 255, 255, 0.8)'
                }}
              ></div>
            </div>

            {/* Project Label */}
            <div 
              className={`text-right transition-all duration-300 ${
                isTransitioning ? 'translate-x-4 opacity-0' : 'translate-x-0 opacity-100'
              }`}
            >
              <div className="text-2xl font-bold opacity-60">
                {currentData.totalProjects}
              </div>
              <div className="text-sm opacity-70 font-medium">
                {currentData.label}{currentData.totalProjects > 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicators */}
        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-1 hidden">
          {projectData.map((_, index) => (
            <div
              key={index}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-white opacity-100' : 'bg-white opacity-30'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default Progress